import { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import fetch from '@api/fetchers';
import { segment } from '@actions/analytics';
import { useSnackbar } from '@stores/snackbar';
import Image from '@base/Image';
import Button from '@base/Button/Button';
import CheckBig from '@icon/CheckBig';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import { emptyList } from '@constants/empty';

import { pluralize } from '@utils/textHelper';
import SearchableDropdown from '@comp/Dropdown/SearchableDropdown';
import {
  subscriptionSubTypes,
  subscriptionSubTypesObj,
  subscriptionTypesObj,
  subscriptionTypes
} from '@constants/index';
import { businessPlatform } from '@utils/constants';
import { getCityRequestEndpoint } from '../apiHelper';
import { CHECKOUT_CITY_SELECTED, CHECKOUT_CITY_SUGGESTED } from '../constants';

const Wrapper = styled.div`
  flex-grow: 1;
`;

const Heading = styled(H3)`
  max-width: 30%;
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    max-width: 100%;
  }
`;

const EnterCitySection = styled.div`
  border-top: 1px solid rgba(45, 129, 247, 0.1);
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B};
  margin-bottom: 124px;
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const CityListingWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_0B};
`;

const EnterCityHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_2B};
`;

const CustomCityRequestWrapper = styled(EnterCityHeadingWrapper)`
  justify-content: left;
`;

const EnterCityHeading = styled(H5)`
  ${mediaQuery.uptoMobile} {
    max-width: 70%;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
`;

const CityInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  .search-dd-wrapper {
    border: 1px solid var(--color-divider);
    border-radius: ${Spacings.SPACING_1B};
    height: ${Spacings.SPACING_12B};
    display: flex;
    justify-content: space-around;
    .search-dd-input-wrapper {
      width: 90%;
      .search-dd-input {
        background: var(--color-i-base-0);
      }
    }
    .search-dd-options {
      max-height: 276px;
    }
  }
`;

const OtherCityItem = styled.div`
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_0B};
  cursor: pointer;
`;

const ErrorText = styled(P1)`
  color: var(--color-i-red);
`;

const StyButton = styled(Button)``;

const CityCardWrapper = styled.div`
  padding: ${Spacings.SPACING_5B} ${Spacings.SPACING_6B};
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  margin-bottom: ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_5B};
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid
    ${({ $isSelected }) =>
      $isSelected ? 'var(--color-i-black)' : 'var(--color-i-divider)'};
  background-color: var(--color-i-white);
`;

const RadioCheckBig = styled(CheckBig)`
  flex-shrink: 0;
  margin-top: 6px;
`;

const RadioBtn = styled.div`
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  border-radius: 50%;
  border: 2px solid var(--color-divider);
  margin-top: 6px;
  flex-shrink: 0;
`;

const CityCardTextWrapper = styled.div``;

const CityCardTitle = styled(H4)``;

const CityCardDescription = styled(P1)`
  color: var(--color-i-gray-13);
`;

const BottomCard = styled.div`
  position: fixed;
  bottom: ${Spacings.SPACING_0B};
  padding: ${Spacings.SPACING_6B};
  box-shadow: ${Spacings.SPACING_0B} -${Spacings.SPACING_2B} ${Spacings.SPACING_8B}
    var(--color-i-gray-v2);
  display: flex;
  justify-content: space-between;
  width: 616px;
  background: var(--color-i-white);
  ${mediaQuery.uptoMobile} {
    width: 100%;
    left: ${Spacings.SPACING_0B};
  }
`;

const BottomCardHeadingWrapper = styled.div``;

const BottomCardHeading = styled(H5)`
  margin-bottom: ${Spacings.SPACING_2B};
`;

const CentreRequestCard = ({ cityName }) => {
  return (
    <EnterCitySection>
      <CustomCityRequestWrapper>
        <Image
          src="check-circle.svg"
          width={28}
          height={28}
          alt="check rounded"
        />
        <EnterCityHeading>
          {`We’ll notify you when we are about to launch a centre in ${cityName}`}
        </EnterCityHeading>
      </CustomCityRequestWrapper>
    </EnterCitySection>
  );
};

const CityCard = ({ data, selectedCity, setSelectedCity }) => {
  const { uid, name, centres } = data;
  const isSelected = selectedCity.uid === uid;
  return (
    <CityCardWrapper
      $isSelected={isSelected}
      onClick={() => setSelectedCity(data)}
    >
      {isSelected ? (
        <RadioCheckBig size="24px" color="var(--color-i-black)" />
      ) : (
        <RadioBtn />
      )}
      <CityCardTextWrapper>
        <CityCardTitle>{name}</CityCardTitle>
        {centres?.length > 0 && (
          <CityCardDescription>
            {pluralize(centres.length, 'centre')} •
            {centres.map((centre, index) => (
              <span key={centre.uid}>
                {' '}
                {centre.name}
                {index < centres.length - 1 ? ',' : ''}
              </span>
            ))}
          </CityCardDescription>
        )}
      </CityCardTextWrapper>
    </CityCardWrapper>
  );
};

const CitySection = ({
  defaultCity,
  cities,
  handleConfirmCity,
  crmTrackingData,
  otherCitiesList
}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const [isError, setIsError] = useState(false);
  const [cityRequested, setCityRequested] = useState(false);
  const [otherCityOption, setOtherCityOption] = useState({});
  let preSelectedCity = defaultCity;
  const activeCities = useMemo(
    () => cities?.filter(({ status }) => status === 'Active'),
    [cities]
  );
  if (!preSelectedCity) {
    preSelectedCity = activeCities?.find((city) => city.isDefault);
  }
  const [selectedCity, setSelectedCity] = useState(preSelectedCity);

  const { open: showSnackbar } = useSnackbar();

  const remainingCitiesList = otherCitiesList
    ? otherCitiesList.filter(({ city }) => {
        return !activeCities.some((item) => item.name === city);
      })
    : emptyList;
  const subType = subscriptionSubTypesObj[subscriptionSubTypes.OFFLINE];
  const type = subscriptionTypesObj[subscriptionTypes.CENTRE];

  const handleCitySelection = (city) => {
    setSelectedCity(city);
  };

  const onConfirmCity = (city) => {
    handleConfirmCity(city);
    segment.track(CHECKOUT_CITY_SELECTED, {
      selected_city: city.name,
      goal_uid: crmTrackingData?.goalUid,
      batch_group_type: subType,
      business_platform: businessPlatform.UA_OFFLINE,
      subscription_sub_type: subType,
      // Adding new prop here for upgrade flow
      subscription_type: type,
      city_select_flow: 'after_choosing_batch'
    });
  };

  const submitCustomCityRequest = () => {
    fetch(getCityRequestEndpoint(), {
      requestType: 'POST',
      body: { data: { city: otherCityOption.city } }
    })
      .then(() => {
        setCityRequested(true);
      })
      .catch(() => {
        showSnackbar({
          message: 'Something went wrong. Please try again.',
          type: 'error'
        });
      });
    segment.track(CHECKOUT_CITY_SUGGESTED, {
      selected_city: otherCityOption.city,
      goal_uid: crmTrackingData?.goalUid,
      batch_group_type: subType,
      business_platform: businessPlatform.UA_OFFLINE,
      subscription_sub_type: subType,
      // Adding new prop here for upgrade flow
      subscription_type: type
    });
  };

  useEffect(() => {
    if (isLoggedIn && otherCityOption?.city) {
      submitCustomCityRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const customCitySubmit = () => {
    if (!otherCityOption?.city) {
      setIsError(true);
    } else if (!isLoggedIn) {
      openLoginDrawer();
    } else {
      submitCustomCityRequest();
    }
  };

  const renderOtherCityList = (item) => {
    return (
      <OtherCityItem onClick={() => setOtherCityOption(item)}>
        {item.city}
      </OtherCityItem>
    );
  };

  return (
    <Wrapper>
      <Heading>Select your preferred city</Heading>

      <CityListingWrapper $cityRequested={cityRequested}>
        {activeCities?.map((city, index) => (
          <CityCard
            key={String(index)}
            data={city}
            selectedCity={selectedCity}
            setSelectedCity={handleCitySelection}
          />
        ))}
      </CityListingWrapper>

      {cityRequested ? (
        <CentreRequestCard cityName={otherCityOption.city} />
      ) : (
        <EnterCitySection>
          <EnterCityHeadingWrapper>
            <div>
              <EnterCityHeading>
                Don’t see your city in the list?
              </EnterCityHeading>
              <CityCardDescription>
                Let us know where you’d like to see a new Unacademy Centre
              </CityCardDescription>
            </div>

            <ImageWrapper>
              <Image
                src="offline-centres/enter-city.svg"
                layout="fill"
                alt="offline-city"
              />
            </ImageWrapper>
          </EnterCityHeadingWrapper>
          <CityInputWrapper>
            <SearchableDropdown
              placeholder="Enter city name"
              selectedOption={otherCityOption}
              options={remainingCitiesList}
              dropdownItem={renderOtherCityList}
              labelKey="city"
            />
            <StyButton
              label="Submit"
              variant="hollow"
              color="black"
              onClick={customCitySubmit}
            />
          </CityInputWrapper>
          {isError && <ErrorText>Please enter City Name</ErrorText>}
        </EnterCitySection>
      )}

      <BottomCard>
        <BottomCardHeadingWrapper>
          <BottomCardHeading>
            {selectedCity?.name}, {selectedCity?.state}
          </BottomCardHeading>
          {selectedCity?.centres?.length > 0 && (
            <CityCardDescription>
              {pluralize(selectedCity.centres.length, 'centre')}
            </CityCardDescription>
          )}
        </BottomCardHeadingWrapper>
        <Button
          label="Continue"
          color="black"
          onClick={() => onConfirmCity(selectedCity)}
        />
      </BottomCard>
    </Wrapper>
  );
};

export default CitySection;
